import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Container from '@shared/components/Container/Container';
import { Tag } from '@shared/common/types/tags';
import SideMenu from '@shared/components/SideMenu/SideMenu';
import { dashboardSideMenu } from '@components/Layout/Layout';
import Loader from '@shared/components/Loader/Loader';
import { Title } from '@shared/components/typography';
import { TitleTag, TitleVariant } from '@shared/components/typography/Title';
import classNames from 'classnames';
import { checkPermissionAccess } from '@shared/helpers/permissionHelper';
import { useCustomerPermissions } from '../../userManagementPermissions';
import { hasCustomerRouteAccess } from '../../routeAuthorization';
import RoutePaths from '@routePaths';
import { ReactComponent as UsersIcon } from '@shared/assets/icons/raport.svg';
import { useTranslation } from 'react-i18next';
import { RoleType } from '@shared/enums/RoleType';

const AccessControler = (props: { children: React.ReactNode }) => {
  const { children } = props;
  const { pathname } = useLocation();

  const { t } = useTranslation();
  const { isLoading, user, permissionIds, permissionNames, hasAnyManageUsersPermission } = useCustomerPermissions();

  const sideMenu = useMemo(() => [
    ...dashboardSideMenu.map((group) => ({
      ...group,
      links: group.links.filter(({ link }) => checkPermissionAccess(link, [...permissionNames].map((name) => ({
        id: name, name, description: '', parent_id: null, type: RoleType.CustomerPortal,
      })))),
    })).filter(({ links }) => links.length > 0),
    ...(hasAnyManageUsersPermission ? [{
      title: t('userManagement.navigation.users'),
      icon: <UsersIcon />,
      defaultExpanded: true,
      links: [{ text: t('userManagement.navigation.manageUsers'), link: RoutePaths.ManageUsers }],
    }] : []),
  ], [hasAnyManageUsersPermission, permissionNames, t]);

  const hasAccessToApp = permissionNames.size > 0 || hasAnyManageUsersPermission;


  const hasAccessToPage = useMemo(() => {
    if (!hasCustomerRouteAccess(pathname, permissionIds)) return false;
    if (pathname.startsWith('/users/')) return true;
    return checkPermissionAccess(pathname, [...permissionNames].map((name) => ({
      id: name,
      name,
      description: '',
      parent_id: null,
      type: RoleType.CustomerPortal,
    })));
  }, [pathname, permissionIds, permissionNames]);

  if (isLoading) {
    return (
      <Container as={Tag.Main} classes="main-wrapper">
        <div role="status" aria-label={t('common.loading')} aria-busy="true">
          <Loader />
        </div>
      </Container>
    );
  }

  return (
    <>
      {hasAccessToApp && (
        <Container
          as={Tag.Main}
          classes={'main-wrapper'}
        >
          <SideMenu elements={sideMenu} userData={user} />
          {hasAccessToPage ? children :
            <div className="permissions">
              <Title variant={TitleVariant.main} tag={TitleTag.h2}>
                {t('common.accessDenied')}
              </Title>
            </div>}
        </Container>
      )}
      {!hasAccessToApp && (
        <Container
          as={Tag.Main}
          classes={classNames('main-wrapper', {
            'main-wrapper--home': pathname === '/',
          })}
        >
          <div className="permissions">
            <Title variant={TitleVariant.main} tag={TitleTag.h2}>
              {t('common.accessDenied')}
            </Title>
          </div>
        </Container>
      )}
    </>
  );
};

export default AccessControler;
